/**
 * notificationRoute — the singleton pure route mapper (PRP-PLAT-026 FR-3 /
 * GW-8884).
 *
 * User-mandated singleton routing philosophy (design session
 * 20260724-notifications-display, "Routing-free"):
 *
 *   gw-spa will implement ONE singleton mapping (`notificationRoute(context)`
 *   switch on `context.kind`, unknown kind → undefined, never throws) passed
 *   in at the app shell — no per-component route knowledge anywhere.
 *
 * This is that ONE function. It is defined once here and wired once at the
 * app shell (GW-8886, a later task) into gw-ui's `linkTo`/`contextHref`/
 * `onSelectContext` props and `getNotificationToastSpec`'s `linkTo`
 * argument — no other module should switch on `NotificationContext.kind`.
 *
 * MUST NEVER throw: called from the ingest hot path (toast dispatch) and
 * from user-interaction handlers (feed page row/group clicks) — a thrown
 * error there must not break notification delivery or navigation.
 */
import type { NotificationContext } from '@gw/ui';

/**
 * Maps a notification's typed domain pointer to an in-app route.
 *
 * @param context - The notification's `context` field. May be `undefined`
 *   (a notification with no context) or carry an unrecognized `kind` (a
 *   context family added after this function shipped) — both return
 *   `undefined` per FR-3, never throw.
 * @returns The app route to navigate to, or `undefined` when there is no
 *   route (absent context, unknown kind, or a malformed context object).
 */
export function notificationRoute(context: NotificationContext | undefined): string | undefined {
  if (!context || typeof context !== 'object') {
    return undefined;
  }

  try {
    switch (context.kind) {
      case 'mission-run': {
        const { missionId, runId } = context;
        if (!missionId || !runId) return undefined;
        return `/personal/missions/${missionId}/runs/${runId}`;
      }
      case 'token-transaction': {
        return '/organization/transactions';
      }
      case 'app-session': {
        const { sessionId } = context;
        if (!sessionId) return undefined;
        return `/personal/sessions?sessionId=${encodeURIComponent(sessionId)}`;
      }
      case 'dataset-query': {
        const { queryId } = context;
        if (!queryId) return '/personal/queries';
        return `/personal/queries/${queryId}`;
      }
      case 'rfi': {
        const { rfiId } = context;
        if (!rfiId) return undefined;
        return `/personal/rfis/${rfiId}`;
      }
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}
